import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Check, ShoppingCart, Package, ArrowRight, Zap, Users, LayoutGrid,
} from 'lucide-react';

// -----------------------------------------------------------------------------
// Source of truth: /app/backend/pricing_config.py
// Rates: Buyer 5% platform fee ADDED to lead price.
//        Seller 20% commission DEDUCTED from lead price.
//        Rates are identical across all membership tiers.
// -----------------------------------------------------------------------------

const BUYER_PLANS = [
  {
    id: 'starter',
    name: 'Buyer Starter',
    price: 99,
    tagline: 'For independent buyers ready to source qualified opportunities.',
    badge: 'Available now',
    badgeColor: 'bg-blue-50 text-blue-800 border-blue-200',
    cta: 'Apply for Access',
    highlight: true,
    seats: '1 user · 1 profile',
    features: [
      'One user and one buyer profile',
      'Masked lead previews',
      'Buyer demand posting with budget and qualification criteria',
      'Deal rooms and individual lead purchasing',
      'Purchase history',
      'Standard dispute access',
    ],
  },
  {
    id: 'growth',
    name: 'Buyer Growth',
    price: 299,
    tagline: 'For teams managing a shared lead pipeline.',
    cta: 'Choose Growth',
    seats: '3 users · 3 profiles',
    features: [
      'All Starter features',
      'Three users and three buyer profiles',
      'Shared team workflows and role permissions',
      'Saved buying criteria and demand templates',
      'Team purchase reporting',
      'CSV export of purchased leads',
    ],
  },
  {
    id: 'scale',
    name: 'Buyer Scale',
    price: 999,
    tagline: 'For businesses coordinating multiple buying operations.',
    cta: 'Choose Scale',
    seats: '10 users · 10 profiles',
    features: [
      'All Growth features',
      'Ten users and ten buyer profiles',
      'Consolidated reporting across profiles',
      'CRM delivery and API access',
      'Routing and purchasing controls',
      'One scheduled onboarding session (up to 60 minutes)',
      'One monthly account review (up to 30 minutes)',
    ],
  },
  {
    id: 'enterprise',
    name: 'Buyer Enterprise',
    price: 2499,
    priceLabel: 'From $2,499',
    tagline: 'For organisations needing tailored administration and delivery.',
    cta: 'Contact Sales',
    seats: '25 users · 25 profiles (base package)',
    features: [
      'All Scale features',
      'Twenty-five users and twenty-five buyer profiles in base package',
      'Organisation-level reporting and permissions',
      'Named account contact',
      'Up to two hours of onboarding',
      'One monthly account review (up to 60 minutes)',
      'Written implementation scope and agreed support arrangements',
    ],
    note: 'Custom development, bespoke integrations and additional managed services are quoted separately.',
  },
];

const SELLER_PLANS = [
  {
    id: 'starter',
    name: 'Seller Starter',
    price: 99,
    tagline: 'For independent sellers bringing qualified leads to market.',
    badge: 'Available now',
    badgeColor: 'bg-blue-50 text-blue-800 border-blue-200',
    cta: 'Apply for Access',
    highlight: true,
    seats: '1 user · 1 profile',
    features: [
      'One user and one seller profile',
      'Seller verification and source-evidence submission',
      'Lead listing',
      'Buyer-demand access',
      'Deal rooms',
      'Sales and payout history',
      'Standard dispute handling',
    ],
  },
  {
    id: 'growth',
    name: 'Seller Growth',
    price: 299,
    tagline: 'For teams managing multiple lead sources.',
    cta: 'Choose Growth',
    seats: '3 users · 3 profiles',
    features: [
      'All Starter features',
      'Three users and three seller profiles',
      'Team permissions and shared workflows',
      'Bulk upload',
      'Reusable listing templates',
      'Source-level sales reporting',
    ],
  },
  {
    id: 'scale',
    name: 'Seller Scale',
    price: 999,
    tagline: 'For agencies coordinating supply across teams.',
    cta: 'Choose Scale',
    seats: '10 users · 10 profiles',
    features: [
      'All Growth features',
      'Ten users and ten seller profiles',
      'Consolidated reporting across profiles',
      'API ingestion and integrations',
      'Workflow automation',
      'One scheduled onboarding session (up to 60 minutes)',
      'One monthly account review (up to 30 minutes)',
    ],
  },
  {
    id: 'enterprise',
    name: 'Seller Enterprise',
    price: 2499,
    priceLabel: 'From $2,499',
    tagline: 'For organisations needing tailored supply operations.',
    cta: 'Contact Sales',
    seats: '25 users · 25 profiles (base package)',
    features: [
      'All Scale features',
      'Twenty-five users and twenty-five seller profiles in base package',
      'Organisation-level administration and reporting',
      'Named account contact',
      'Up to two hours of onboarding',
      'One monthly account review (up to 60 minutes)',
      'Written implementation scope and agreed support arrangements',
    ],
    note: 'Custom development, bespoke integrations and additional managed services are quoted separately.',
  },
];

const FAQS = [
  {
    q: 'Does posting demand charge my card?',
    a: 'No. Posting demand is included in your membership. A lead payment is triggered when you confirm a purchase, or through automatic purchasing you have explicitly authorised.',
  },
  {
    q: 'Are leads included in my membership?',
    a: 'No. Membership provides access and plan features. Leads are purchased separately.',
  },
  {
    q: 'Do higher plans reduce transaction fees?',
    a: 'No. Buyers pay 5% and sellers pay 20% across every plan. Higher memberships provide more team capacity, tools and service.',
  },
  {
    q: 'Can I buy and sell?',
    a: 'Yes. Activate a buyer membership and a seller membership within your account. Your combined recurring price is shown before you subscribe.',
  },
  {
    q: 'Is a converted sale guaranteed?',
    a: 'No. Leads must meet the agreed qualification criteria, but a sale is not guaranteed. Disputes are handled under the published policy.',
  },
];

export default function PricingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="border-b border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono font-medium mb-3" data-testid="pricing-kicker">
            Membership = access · Leads priced separately
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-gray-900 max-w-3xl" data-testid="pricing-headline">
            One membership. Buy and sell.
            <span className="block text-[#0055FF]">Clear fees.</span>
          </h1>
          <p className="mt-4 text-base text-gray-600 max-w-2xl" data-testid="pricing-sub-1">
            Choose your plan by team size, tools and support. Every membership includes access to buy and sell leads.
          </p>
          <p className="mt-3 text-base text-gray-600 max-w-2xl" data-testid="pricing-sub-2">
            Buyers pay <span className="font-mono font-semibold text-[#0055FF]">5%</span> on top of the lead price. Sellers pay <span className="font-mono font-semibold text-[#0055FF]">20%</span> from the sale price. The same rates apply on every plan.
          </p>
        </div>
      </section>

      {/* Fee headline strip */}
      <section className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <FeeCard
            side="Buyer"
            pct="5%"
            desc="added to the lead price at checkout"
            testid="fee-headline-buyer"
          />
          <FeeCard
            side="Seller"
            pct="20%"
            desc="deducted from the lead sale price on payout"
            testid="fee-headline-seller"
          />
          <div className="border border-gray-200 rounded-lg p-5 bg-gray-50" data-testid="fee-headline-example">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-mono mb-2">A$300 lead example</div>
            <ul className="text-sm text-gray-800 space-y-1 font-mono">
              <li>Lead price: <span className="font-semibold">A$300</span></li>
              <li>Buyer pays: <span className="font-semibold text-[#0055FF]">A$315</span> <span className="text-xs text-gray-500">(+A$15 · 5%)</span></li>
              <li>Seller receives: <span className="font-semibold text-gray-900">A$240</span> <span className="text-xs text-gray-500">(−A$60 · 20%)</span></li>
              <li className="pt-1 border-t border-gray-200 mt-1">Platform revenue: <span className="font-semibold">A$75</span></li>
            </ul>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <Tabs defaultValue="buyer" data-testid="pricing-tabs">
          <TabsList className="bg-gray-100 p-1">
            <TabsTrigger value="buyer" className="data-[state=active]:bg-white" data-testid="pricing-tab-buyer">
              <ShoppingCart className="w-4 h-4 mr-2" /> Buyer Memberships
            </TabsTrigger>
            <TabsTrigger value="seller" className="data-[state=active]:bg-white" data-testid="pricing-tab-seller">
              <Package className="w-4 h-4 mr-2" /> Seller Memberships
            </TabsTrigger>
            <TabsTrigger value="fees" className="data-[state=active]:bg-white" data-testid="pricing-tab-fees">
              <Zap className="w-4 h-4 mr-2" /> Transaction Fees
            </TabsTrigger>
          </TabsList>

          <TabsContent value="buyer" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="buyer-plans-grid">
              {BUYER_PLANS.map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  onCta={() => plan.id === 'enterprise' ? navigate('/contact?plan=enterprise&role=buyer') : navigate('/register?role=buyer&plan=' + plan.id)}
                />
              ))}
            </div>
            <CalloutPanel
              icon={<Users className="w-5 h-5 text-[#0055FF]" strokeWidth={2} />}
              title="Higher tiers = more people and profiles, not cheaper leads"
              body="Every plan pays the same 5% platform fee on purchase. What scales is your team capacity, reporting depth, and delivery options."
            />
          </TabsContent>

          <TabsContent value="seller" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="seller-plans-grid">
              {SELLER_PLANS.map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  onCta={() => plan.id === 'enterprise' ? navigate('/contact?plan=enterprise&role=seller') : navigate('/register?role=seller&plan=' + plan.id)}
                />
              ))}
            </div>
            <CalloutPanel
              icon={<LayoutGrid className="w-5 h-5 text-[#0055FF]" strokeWidth={2} />}
              title="The commission never changes with plan"
              body="Sellers keep 80% of every sale on every tier. Higher plans unlock bulk uploads, more sources, integrations and account service — not a lower clip."
            />
          </TabsContent>

          <TabsContent value="fees" className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-3">
                <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="fees-table">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                        <th className="px-6 py-4">Party</th>
                        <th className="px-6 py-4">Fee</th>
                        <th className="px-6 py-4">Applied</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 font-medium">Buyer</td>
                        <td className="px-6 py-4 font-mono font-semibold text-[#0055FF]">5%</td>
                        <td className="px-6 py-4 text-gray-700">Added to lead price at checkout</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 font-medium">Seller</td>
                        <td className="px-6 py-4 font-mono font-semibold text-[#0055FF]">20%</td>
                        <td className="px-6 py-4 text-gray-700">Deducted from sale price on payout</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 border border-gray-200 rounded-lg p-6 bg-white" data-testid="worked-example">
                  <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-mono mb-2">Worked example — A$300 lead</div>
                  <div className="grid grid-cols-2 gap-4 text-sm font-mono">
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Buyer</div>
                      <div>Lead price: <span className="font-semibold">A$300</span></div>
                      <div>Platform fee (5%): <span className="font-semibold">A$15</span></div>
                      <div className="mt-1 pt-1 border-t border-gray-200">
                        Buyer charged: <span className="text-[#0055FF] font-semibold">A$315</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Seller</div>
                      <div>Sale price: <span className="font-semibold">A$300</span></div>
                      <div>Commission (20%): <span className="font-semibold">A$60</span></div>
                      <div className="mt-1 pt-1 border-t border-gray-200">
                        Seller receives: <span className="text-gray-900 font-semibold">A$240</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-gray-600">
                    Platform revenue on this transaction: <span className="font-mono font-semibold">A$75</span>.
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 bg-gray-900 text-white rounded-lg p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-blue-300 font-mono mb-3">
                  Why flat rates
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3">The fee doesn't punish smaller buyers or reward bigger ones.</h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                  Volume-scaled commission encourages sellers to route their best leads to the largest customer, not the best-fit customer. A flat 20% on the seller side and 5% on the buyer side keeps matching honest. You pay for a plan for capacity and service, and for leads on their actual quality.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* FAQ */}
        <section className="mt-16" data-testid="pricing-faq">
          <h2 className="font-heading text-2xl font-semibold text-gray-900 mb-6">Common questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FAQS.map((f, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-5 bg-white" data-testid={`pricing-faq-${i}`}>
                <div className="font-heading text-base font-semibold text-gray-900">{f.q}</div>
                <div className="mt-2 text-sm text-gray-600 leading-relaxed">{f.a}</div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="mt-16 bg-white border border-gray-200 rounded-lg p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="pricing-footer-cta">
          <div>
            <h3 className="font-heading text-xl font-semibold text-gray-900">Ready to buy or sell business leads?</h3>
            <p className="text-sm text-gray-600 mt-1">
              One membership gives you access to both. Choose the plan that fits your business, with clear transaction fees across every tier.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/register?plan=starter')} className="bg-[#0055FF] hover:bg-[#0044CC] text-white h-11" data-testid="cta-pricing-get-started">
              Get Started
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const FeeCard = ({ side, pct, desc, testid }) => (
  <div className="border border-gray-200 rounded-lg p-5 bg-white" data-testid={testid}>
    <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-mono mb-2">{side}</div>
    <div className="flex items-baseline gap-2">
      <span className="font-mono text-4xl font-semibold text-gray-900">{pct}</span>
      <span className="text-sm text-gray-600">{desc}</span>
    </div>
  </div>
);

const PlanCard = ({ plan, onCta }) => (
  <div className={`relative bg-white border rounded-lg p-6 flex flex-col ${
    plan.highlight ? 'border-[#0055FF] shadow-sm' : 'border-gray-200'
  }`} data-testid={`plan-${plan.id}`}>
    {plan.badge && (
      <span className={`inline-flex items-center gap-1 text-xs font-mono font-semibold px-2 py-0.5 border rounded self-start mb-3 ${plan.badgeColor}`}>
        {plan.badge}
      </span>
    )}
    <h3 className="font-heading text-xl font-semibold text-gray-900">{plan.name}</h3>
    <p className="text-xs text-gray-500 mt-1 min-h-[32px]">{plan.tagline}</p>

    <div className="mt-4 flex items-baseline gap-1">
      {plan.priceLabel ? (
        <>
          <span className="font-mono text-3xl font-semibold text-gray-900">{plan.priceLabel}</span>
          <span className="text-sm text-gray-500">/month</span>
        </>
      ) : (
        <>
          <span className="font-mono text-4xl font-semibold text-gray-900">${plan.price}</span>
          <span className="text-sm text-gray-500">/month</span>
        </>
      )}
    </div>
    {plan.seats && (
      <div className="text-xs text-gray-500 font-mono mt-1">{plan.seats}</div>
    )}

    <ul className="mt-5 space-y-2 flex-1">
      {plan.features.map((f, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
          <Check className="w-4 h-4 text-[#0055FF] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
          <span>{f}</span>
        </li>
      ))}
    </ul>

    {plan.note && (
      <div className="mt-4 bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-600">
        {plan.note}
      </div>
    )}

    <Button
      onClick={onCta}
      className={`mt-5 h-11 ${
        plan.highlight ? 'bg-[#0055FF] hover:bg-[#0044CC] text-white' :
        'bg-gray-900 hover:bg-gray-800 text-white'
      }`}
      data-testid={`plan-cta-${plan.id}`}
    >
      {plan.cta}
    </Button>
  </div>
);

const CalloutPanel = ({ title, body, icon }) => (
  <div className="mt-10 bg-gray-50 border border-gray-200 rounded-lg p-6 flex gap-4">
    <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
      {icon || <Zap className="w-5 h-5 text-[#0055FF]" strokeWidth={2} />}
    </div>
    <div>
      <h4 className="font-heading text-base font-semibold text-gray-900">{title}</h4>
      <p className="text-sm text-gray-600 mt-1 leading-relaxed">{body}</p>
    </div>
  </div>
);
